import {
  forwardRef,
  useEffect,
  useRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { gsap } from '@/lib/gsap';
import { cn } from '@/lib/cn';
import { prefersReducedMotion } from '@/lib/lenis';

type Variant = 'primary' | 'secondary' | 'ghost' | 'gold' | 'danger' | 'glass';
type Size = 'sm' | 'md' | 'lg';

interface CommonButtonProps {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Disables magnetic hover (auto-disabled on mobile/reduced-motion). */
  noMagnetic?: boolean;
  /** Full-width container. */
  block?: boolean;
  className?: string;
  children?: ReactNode;
}

type NativeButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonButtonProps | 'type'> & {
  type?: 'button' | 'submit' | 'reset';
  to?: undefined;
  href?: undefined;
};
type RouterLinkProps = Omit<LinkProps, keyof CommonButtonProps | 'to'> & {
  /** When provided, renders a react-router <Link> styled as a Button. */
  to: LinkProps['to'];
  href?: undefined;
  type?: undefined;
};
type AnchorProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonButtonProps | 'href'> & {
  /** When provided, renders an <a> styled as a Button (external links). */
  href: string;
  to?: undefined;
  type?: undefined;
};

export type ButtonProps = CommonButtonProps & (NativeButtonProps | RouterLinkProps | AnchorProps);

const variantStyles: Record<Variant, string> = {
  primary:
    'bg-[color:var(--color-brand-magenta)] text-white hover:bg-[color:var(--color-brand-magenta-light)] shadow-[var(--shadow-magenta-glow)]',
  secondary:
    'border border-[color:var(--color-brand-magenta)] text-[color:var(--color-brand-magenta)] hover:bg-[color:var(--color-brand-magenta)]/10',
  ghost:
    'text-[color:var(--color-text-secondary)] hover:text-white hover:bg-white/5',
  gold:
    'bg-gradient-to-br from-[color:var(--color-gold)] to-[color:var(--color-gold-light)] text-[color:var(--color-bg-deep)] font-semibold shadow-[var(--shadow-gold-glow)] hover:brightness-110',
  danger:
    'bg-[color:var(--color-error)] text-white hover:bg-[color:var(--color-error)]/85',
  glass:
    'glass-card text-white hover:bg-white/10',
};

const sizeStyles: Record<Size, string> = {
  sm: 'h-9 px-4 text-[13px] rounded-md gap-2',
  md: 'h-11 px-6 text-[14px] rounded-md gap-2',
  lg: 'h-14 px-8 text-[15px] rounded-md gap-3',
};

const Spinner = () => (
  <span
    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
    aria-hidden
  />
);

const buttonShellClass = (variant: Variant, size: Size, block: boolean, className?: string) =>
  cn(
    'magnetic-host relative inline-flex items-center justify-center font-[family-name:var(--font-body)] font-medium tracking-wide',
    'transition-[color,background-color,border-color,box-shadow,filter] duration-300 ease-out',
    'disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand-magenta)]',
    sizeStyles[size],
    variantStyles[variant],
    block && 'w-full',
    className
  );

/**
 * useMagnetic — applies the magnetic-hover effect to the host element.
 * Shared between the <button>, <a>, and <Link> render branches so all three
 * feel identical. Auto-skips on touch / reduced motion / noMagnetic.
 */
function useMagnetic(
  hostRef: React.RefObject<HTMLElement | null>,
  innerRef: React.RefObject<HTMLSpanElement | null>,
  noMagnetic: boolean
): void {
  useEffect(() => {
    const el = hostRef.current;
    const inner = innerRef.current;
    if (!el || !inner) return;
    if (noMagnetic) return;
    if (prefersReducedMotion()) return;
    if (typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches) return;

    const xTo = gsap.quickTo(el, 'x', { duration: 0.4, ease: 'expo.out' });
    const yTo = gsap.quickTo(el, 'y', { duration: 0.4, ease: 'expo.out' });
    const ixTo = gsap.quickTo(inner, 'x', { duration: 0.5, ease: 'expo.out' });
    const iyTo = gsap.quickTo(inner, 'y', { duration: 0.5, ease: 'expo.out' });

    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const dx = e.clientX - (rect.left + rect.width / 2);
      const dy = e.clientY - (rect.top + rect.height / 2);
      xTo(dx * 0.25);
      yTo(dy * 0.4);
      ixTo(dx * 0.12);
      iyTo(dy * 0.18);
    };
    const onLeave = () => {
      xTo(0); yTo(0); ixTo(0); iyTo(0);
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [noMagnetic, hostRef, innerRef]);
}

interface ButtonInnerProps {
  isLoading: boolean;
  leftIcon: ReactNode;
  rightIcon: ReactNode;
  children: ReactNode;
  innerRef: React.RefObject<HTMLSpanElement | null>;
}
function ButtonInner({ isLoading, leftIcon, rightIcon, children, innerRef }: ButtonInnerProps) {
  return (
    <span ref={innerRef} className="inline-flex items-center gap-[inherit] pointer-events-none">
      {isLoading ? <Spinner /> : leftIcon}
      <span>{children}</span>
      {rightIcon}
    </span>
  );
}

/**
 * Polymorphic Button.
 *
 *   <Button onClick={...}>...</Button>      → <button>
 *   <Button to="/products">...</Button>     → <Link> (react-router)
 *   <Button href="https://x.com">...</Button> → <a target="_blank" rel="..."> if external
 *
 * Always renders a single focusable element. Never wrap this in another Link;
 * doing so creates nested interactive elements and produces a redundant tab stop.
 */
/** Strip Button-specific props from any rest spread so they never leak to the DOM. */
const PRESENTATION_KEYS = [
  'variant', 'size', 'isLoading', 'leftIcon', 'rightIcon',
  'noMagnetic', 'block', 'className', 'children',
] as const;
function stripPresentationProps<T extends Record<string, unknown>>(o: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k in o) {
    if (!(PRESENTATION_KEYS as readonly string[]).includes(k)) out[k] = o[k];
  }
  return out;
}

export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(props, ref) {
  const {
    variant = 'primary',
    size = 'md',
    isLoading = false,
    leftIcon,
    rightIcon,
    noMagnetic = false,
    block = false,
    className,
    children,
  } = props;

  const innerRef = useRef<HTMLSpanElement | null>(null);
  const hostRef = useRef<HTMLElement | null>(null);

  // Combine forwarded + local ref
  useEffect(() => {
    if (typeof ref === 'function') ref(hostRef.current);
    else if (ref) (ref as { current: HTMLElement | null }).current = hostRef.current;
  }, [ref]);

  useMagnetic(hostRef, innerRef, noMagnetic);

  const shell = buttonShellClass(variant, size, block, className);
  const inner = (
    <ButtonInner isLoading={isLoading} leftIcon={leftIcon} rightIcon={rightIcon} innerRef={innerRef}>
      {children}
    </ButtonInner>
  );

  // ── Anchor (external href) ────────────────────────────────────────────────
  if ('href' in props && props.href !== undefined) {
    const { href, target, rel, ...rest } = props as AnchorProps;
    const isExternal = /^https?:/.test(href);
    const safeRest = stripPresentationProps(rest as Record<string, unknown>);
    return (
      <a
        ref={(el) => { hostRef.current = el; }}
        href={href}
        target={target ?? (isExternal ? '_blank' : undefined)}
        rel={rel ?? (isExternal ? 'noopener noreferrer' : undefined)}
        className={shell}
        aria-disabled={isLoading || undefined}
        {...(safeRest as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {inner}
      </a>
    );
  }

  // ── Router Link (internal `to`) ──────────────────────────────────────────
  if ('to' in props && props.to !== undefined) {
    const { to, ...rest } = props as RouterLinkProps;
    const safeRest = stripPresentationProps(rest as Record<string, unknown>);
    return (
      <Link
        ref={(el) => { hostRef.current = el; }}
        to={to}
        className={shell}
        aria-disabled={isLoading || undefined}
        {...(safeRest as Omit<LinkProps, 'to' | 'className'>)}
      >
        {inner}
      </Link>
    );
  }

  // ── Native button (default) ──────────────────────────────────────────────
  const {
    type = 'button',
    disabled,
    onMouseMove,
    onMouseLeave,
    ...rest
  } = props as NativeButtonProps;
  const safeRest = stripPresentationProps(rest as Record<string, unknown>);
  return (
    <button
      ref={(el) => { hostRef.current = el; }}
      type={type}
      disabled={disabled || isLoading}
      className={shell}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      {...(safeRest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {inner}
    </button>
  );
});
